import React from 'react';
import { Link } from 'react-router-dom';
import HtmlContent from '../../../../components/common/HtmlContent';
import useHashScroll from '../../../../hooks/useHashScroll';

/**
 * StoryboardingPage Component
 * 
 * Displays all storyboarding tools content like the legacy HTML site
 */
const StoryboardingPage = () => {
  // Use the hash scroll hook to scroll to the correct section
  useHashScroll();
  
  // Storyboarding intro content from the legacy HTML
  const storyboardingIntroHtml = `
    <p>
    A visually interesting method of building empathy in to the ideation and design process.
    </p>
        
    <p>
    Storyboarding can use our persona and literally put them in the scenario where their problem occurs, combined with a solution that results in the a positive outcome.
    </p>
  `;

  // Templates HTML for each tool
  const conceptExamplesTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/design/ideation/storyboarding/storyboarding-examples.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/uXjVMwDonCA=/" className="link-table">Miro</a>
    </>
  );

  const outcomeStatementTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/design/ideation/storyboarding/outcome-statement.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/uXjVM7UNW1I=/" className="link-table">Miro</a>
    </>
  );

  const theStoryboardTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/design/ideation/storyboarding/the-storyboard.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/uXjVM6h5QYo=/" className="link-table">Miro</a>
    </>
  );

  const aiStoryboardingTemplates = (
    <>
      <i className="far fa-file-pdf">&nbsp;&nbsp;</i>
      <a href="/design/ideation/storyboarding/storyboarding-concept.pdf" className="link-table">PDF</a>&nbsp;&nbsp;
      <i className="fas fa-chalkboard"></i>&nbsp;&nbsp;
      <a href="https://miro.com/app/board/o9J_kpGUsJk=/" className="link-table">Miro</a>
    </>
  );

  // Helper function to render tool info table with optional creator row
  const renderToolInfoTable = (category, updated, templates, creator = null) => {
    return (
      <div className="tool-table-wrap">
        <table id="tool-table">
          <tbody>
            <tr>
              <th colSpan="3">Tool Info</th>
            </tr>
            <tr>
              <td>Category</td>
              <td>{category}</td>  
            </tr>
            <tr>
              <td>Updated</td>
              <td>{updated}</td>
            </tr>
            <tr>
              <td>{templates === conceptExamplesTemplates ? 'Examples' : 'Templates'}</td>
              <td>{templates}</td>
            </tr>
            {creator && (
              <tr>
                <td>Creator</td>
                <td>{creator}</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <>
      {/* Storyboarding Intro */}
      <div id="section-tools-storyboarding" className="tool-overview-container">
        <div className="tool-breadcrumb-container">
          <div className="tool-breadcrumb-wrap">
            <div className="tool-breadcrumb-icon">
              <i className="fas fa-bullhorn fa-2x"></i>
            </div>
            <div className="tool-breadcrumb-nav">
              <Link to="/design">Design Tools</Link>&nbsp;&nbsp;
              <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
              <Link to="/design/ideation">Ideation</Link>
            </div>
            <div className="tool-breadcrumb-title">
              <Link to="/design/ideation/storyboarding">Storyboarding</Link>
            </div>
          </div>
        </div>
        <div className="tool-overview-para">
          <HtmlContent html={storyboardingIntroHtml} containerClass="" />
        </div>
        <div className="tool-section-container">
          <div className="tool-section-wrap">
            <div className="tool-section-title">Section Tools</div>
          </div>
          <div className="tool-section-list">
            <div><Link className="tool-section-cta" to="#concept">Concept</Link></div>
            <div><Link className="tool-section-cta" to="#outcome-statement">Outcome Statement</Link></div>
            <div><Link className="tool-section-cta" to="#the-storyboard">The Storyboard</Link></div>
            <div><Link className="tool-section-cta" to="#ai-storyboarding">AI Storyboarding</Link></div>
          </div>
        </div>
      </div>

      {/* Concept Section */}
      <main className="case-study">
        <span className="case-tool-title" id="concept">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="concept">
            <span className="tool-title-number">
              <Link to="/design/ideation/storyboarding/#concept">Concept</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/design/ideation">Ideation</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/design/ideation/storyboarding">Storyboarding</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/design/ideation/storyboarding/#concept"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-concept" 
            id="tab-concept-overview" 
            defaultChecked
          />
          <label htmlFor="tab-concept-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Storyboarding", "05/08/23", conceptExamplesTemplates)}
              <div className="tool-opening">
                <p>
                Storyboarding is particularly helpful during storyboarding as a means of understanding and emphasising with a real world user problem. 
                </p>
            
                <p>
                By visually showing the steps a user takes to complete a task, it can help further build understanding in a very simple but quick way - aiding the ideation process. 
                </p>
            
                <p>
                Pioneered by Disney, Storyboarding pinned images to a board which then allowed for quick editing and changes of the shots during filmmaking - this same idea translates well to product ideation. 
                </p>
            
                <p>
                By visualising the steps a user would normally go through in order to accomplish a task, the problems they may face, or the potential user experience we are ideating for, we build empathy and understanding. 
                </p>    

                <p>
                In this instance, we use it to help fill out the product after the initial ideation concepts / ideas / innovation has taken place - this will then also help inform the various user Flows as we move through the ideation phase.  
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.nngroup.com/articles/storyboards-visualize-ideas/" className="link-table">Storyboards Help Visualize UX Ideas - Nielsen Norman Group</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.smashingmagazine.com/2017/10/storyboarding-ux-ideation/" className="link-table">The Role Of Storyboarding In UX ideation - Smashing Magazine</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://uxstudioteam.com/ux-blog/ux-storyboard/" className="link-table">UX Storyboard Creation: A Complete Guide For Beginners - UX Studio Team</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.uxmatters.com/mt/archives/2014/03/storymapping-a-macgyver-approach-to-content-strategy-part-2.php" className="link-table">Storymapping: A MacGyver Approach to Content Strategy, Part 2 - UX Matters</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-concept" 
            id="tab-concept-template"
          />
          <label htmlFor="tab-concept-template">Examples</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/uXjVMwDonCA=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Storyboarding Concept Examples"
            ></iframe>
          </div>
        </div>
      </main>

      {/* Outcome Statement Section */}
      <main className="case-study">
        <span className="case-tool-title" id="outcome-statement">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="outcome-statement">
            <span className="tool-title-number">
              <Link to="/design/ideation/storyboarding/#outcome-statement">Outcome Statement</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/design/ideation">Ideation</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/design/ideation/storyboarding">Storyboarding</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/design/ideation/storyboarding/#outcome-statement"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-outcome-statement" 
            id="tab-outcome-statement-overview" 
            defaultChecked
          />
          <label htmlFor="tab-outcome-statement-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Storyboarding", "05/08/23", outcomeStatementTemplates)}
              <div className="tool-opening">
                <p>
                An Outcome Statement helps provide context, empathy, and understanding prior to creating our Storyboard. 
                </p>
            
                <p>
                It helps sum up where we are at after our research and ideation phase, along with a desired outcome.  
                </p>
            
                <p>
                Firstly we take the persona we are designing for - <b>Situation</b>
                </p>
            
                <p>
                Then we add context to the persona's situation - <b>Problem</b>
                </p>    

                <p>
                From here we take our design from the ideation phase - <b>Solution</b> 
                </p>
            
                <p>               
                Finally we can decide on the desired user goal - <b>Outcome</b>
                </p>              

                <p>
                This simpleness of plotting things out this way goes hand in hand with our Storyboard. You can almost start to see the story take shape before even starting.
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.cubyts.com/resources/blogs/why-choose-ux-outcomes-over-business-outcome#" className="link-table">Why Choose UX Outcomes over Business Outcome - Cubyts</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://www.nngroup.com/articles/outcomes-vs-features/" className="link-table">Minimize Design Risk by Focusing on Outcomes not Features - Nielsen Norman Group</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-outcome-statement" 
            id="tab-outcome-statement-template"
          />
          <label htmlFor="tab-outcome-statement-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/uXjVM7UNW1I=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="Outcome Statement Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* The Storyboard Section */}
      <main className="case-study">
        <span className="case-tool-title" id="the-storyboard">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="the-storyboard">
            <span className="tool-title-number">
              <Link to="/design/ideation/storyboarding/#the-storyboard">The Storyboard</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/design/ideation">Ideation</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/design/ideation/storyboarding">Storyboarding</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/design/ideation/storyboarding/#the-storyboard"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-the-storyboard" 
            id="tab-the-storyboard-overview" 
            defaultChecked
          />
          <label htmlFor="tab-the-storyboard-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Storyboarding", "05/08/23", theStoryboardTemplates)}
              <div className="tool-opening">
                <p>
                Storyboarding can come in a number of forms, but is generally a series of Sketches that follows a Story Arc. 
                </p>
            
                <p>
                Before we create our Storyboard, we must remind ourselves that this is a story, and every good story has a beginning, a middle and an end. 
                </p>

                <h4>Narrative Arc</h4>
            
                <p>
                The Narrative Arc is a useful tool for plotting the various parts of a story, we can break this down alongside the Outcome Statement; 
                </p>

                <p>
                    <span className="underline">Situation</span><br />

                    <ul className="bullet-tk">
                        <li>Exposition: Beginning of the story, introduces characters and setting.</li>
                    </ul>

                    <span className="underline">Problem</span><br />

                    <ul className="bullet-tk">
                        <li>Inciting Incident: Event that sparks the main conflict.</li>
                        <li>Rising Action: Series of events that intensify the story.</li>
                        <li>Crisis: Crucial moment with a major obstacle or decision.</li>
                    </ul>

                    <span className="underline">Solution</span><br />

                    <ul className="bullet-tk">
                        <li>Climax: Peak conflict, determines the outcome.</li>
                        <li>Denouement or Falling Action: Events after the climax, wrapping up loose ends.</li>
                    </ul>

                    <span className="underline">Outcome</span><br />

                    <ul className="bullet-tk">
                        <li>Resolution: Final part, resolves conflicts and provides closure.</li>
                    </ul>
                </p>

                <h4>The Narrative Arc Visualised</h4>

                <br />

                <iframe className="miro_embed" src="https://miro.com/app/embed/uXjVM6h5QYo=/?&pres=1" frameBorder="0" scrolling="no" allowFullScreen></iframe>

                <br />

                <h4>Process</h4>

                <p>
                    <ul className="bullet-tk">
                        <li>Remind ourselves of the User Persona we are designing for by drawing them along with their situation.</li>
                        <li>Write down the Scenario we are designing for.</li>
                        <li>Also take the User/Job Story we are designing for.</li>
                        <li>Take our Outcome Statement as a finals means of reminding ourselves what we are storyboarding out.</li>
                        <li>Take a pen and paper.</li>
                        <li>Draw out the plot steps or flow that a user would experience through the Story - use the Narrative Arc to help craft this.</li>
                        <li>Visually Create the Storyboard cells following the steps.</li>
                        <li>Add captions to explain each cell along with emoticons.</li>
                    </ul> 
                </p>    

                <h4>Notes</h4>

                <p>
                    <ul className="bullet-tk">
                        <li>Include as many or as little cells/scenes are you feel is necessary.</li>
                        <li>The narrative arc is very helpful for crafting the story, use that and remember the various parts of the Outcome Statement.</li>
                        <li>Remember to stick to the core user situation and problem.</li>
                        <li>Illustrate in a way that can be built on.</li>
                        <li>By focusing on these steps, we can keep the product lean.</li>
                    </ul> 
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://media.nngroup.com/media/articles/attachments/Storyboard-Template.pdf" className="link-table">Storyboard Template - Nielsen Norman Group</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-the-storyboard" 
            id="tab-the-storyboard-template"
          />
          <label htmlFor="tab-the-storyboard-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/uXjVM7bK0PA=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="The Storyboard Template"
            ></iframe>
          </div>
        </div>
      </main>

      {/* AI Storyboarding Section */}
      <main className="case-study">
        <span className="case-tool-title" id="ai-storyboarding">&nbsp;&nbsp;</span>
        <div className="linebreak"></div>
        <div className="tool-title-wrap">
          <div className="tool-title-icon"><i className="fas fa-tools fa-2x"></i></div>
          <div className="tool-title" id="ai-storyboarding">
            <span className="tool-title-number">
              <Link to="/design/ideation/storyboarding/#ai-storyboarding">AI Storyboarding</Link>
            </span>
          </div>
          <div className="tool-title-nav">
            <Link to="/design/ideation">Ideation</Link>&nbsp;&nbsp;
            <i className="fas fa-angle-double-right"></i>&nbsp;&nbsp;
            <Link to="/design/ideation/storyboarding">Storyboarding</Link>
          </div>
          <div className="tool-title-link">
            <Link to="/design/ideation/storyboarding/#ai-storyboarding"><i className="fas fa-link fa-2x"></i></Link>
          </div>
        </div>
        <div className="linebreak"></div>
        <div className="tabs">
          <input 
            type="radio" 
            name="tabs-ai-storyboarding" 
            id="tab-ai-storyboarding-overview" 
            defaultChecked
          />
          <label htmlFor="tab-ai-storyboarding-overview">Overview</label>
          <div className="tab tab-tools">
            <div className="tool-opening-container">
              {renderToolInfoTable("Storyboarding", "05/08/23", aiStoryboardingTemplates)}
              <div className="tool-opening">
                <p>
                Along with AI Sketching, Storyboarding using AI can help us in different ways. By crafting a prompt in a certain way, it can almost open us up to even further empathy than we might normally imagine.
                </p>
            
                <p>
                Like anything else generated by AI, this is purely for inspiration, speed and knowledge gaps, and it should be iterated on and built on to help us along the design path, rather than completely relied on. 
                </p>

                <h4>AI Services</h4>
            
                <p>
                There are a number of potential services that could help us - all we really need to do is create prompts that generate images that help us to tell our story which could help us get to where we want to be faster.
                </p>  

                <p>
                <a href="https://krock.io/storyboard-ai/" className="link-table">Krock.io</a> - This is generally the front runner for AI storyboarding, but it is a bit more skewed to traditional uses of Storyboarding. 
                </p>              

                <p>
                <a href="https://www.midjourney.com" className="link-table">Midjourney</a> - Currently the best AI image generator, with tons of options to refine etc and quite speedy. For individual frames, this is probably the best solution. 
                </p>

                <h4>Process</h4>

                <p>
                    <ul className="bullet-tk">
                        <li>Remind ourselves of the User Persona we are designing for by drawing them along with their situation.</li>
                        <li>Write down the Scenario we are designing for.</li>
                        <li>Also take the User/Job Story we are designing for.</li>
                        <li>Take our Outcome Statement as a finals means of reminding ourselves what we are storyboarding out.</li>
                        <li>Take a pen and paper.</li>
                        <li>Draw out the plot steps or flow that a user would experience through the Story - use the Narrative Arc to help craft this.</li>
                        <li>Using our Prompt worksheet as a guide, create the various prompts to generate each cell of our story.</li>
                        <li>Iterate accordingly.</li>
                        <li>Copy and paste the generated images in to the template cells in order.</li>
                        <li>Add captions to explain each cell along with emoticons.</li>
                    </ul> 
                </p>
              </div>
            </div>
            <div className="tool-further-container">
              <div className="tool-further-wrap">
                <table id="tool-table">
                  <tbody>
                    <tr>
                      <th>Further Resources</th>
                    </tr>
                    <tr>
                      <td>
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://bettermarketing.pub/storyboarding-with-ai-d1534c4d91d5" className="link-table">Storyboarding with AI - Better Marketing</a>
                        <br />
                        <i className="fas fa-arrow-alt-circle-right"></i>&nbsp;&nbsp;
                        <a href="https://krock.io/storyboard-ai-promts/" className="link-table">Storyboard AI Prompts</a>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <input 
            type="radio" 
            name="tabs-ai-storyboarding" 
            id="tab-ai-storyboarding-template"
          />
          <label htmlFor="tab-ai-storyboarding-template">Template</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/uXjVM6z6wYg=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="AI Storyboarding Template"
            ></iframe>
          </div>

          <input 
            type="radio" 
            name="tabs-ai-storyboarding" 
            id="tab-ai-storyboarding-example"
          />
          <label htmlFor="tab-ai-storyboarding-example">Example Artifact</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/o9J_kpGUsJk=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="AI Storyboarding Example Artifact"
            ></iframe>
          </div>

          <input 
            type="radio" 
            name="tabs-ai-storyboarding" 
            id="tab-ai-storyboarding-prompt-worksheet"
          />
          <label htmlFor="tab-ai-storyboarding-prompt-worksheet">Prompt Worksheet</label>
          <div className="tab tab-tools">
            <iframe 
              className="miro_embed" 
              src="https://miro.com/app/embed/uXjVM7lSU_Y=/?&pres=1" 
              frameBorder="0" 
              scrolling="no" 
              allowFullScreen
              title="AI Storyboarding Prompt Worksheet"
            ></iframe>
          </div>
        </div>
      </main>
    </>
  );
};

export default StoryboardingPage; 